"use client"
import { zodResolver } from "@hookform/resolvers/zod"
import type { FragmentOf } from "@opensea/graphql"
import { graphql, readFragment } from "@opensea/graphql"
import { Button } from "@opensea/next-ui-kit"
import { FlexCenter, FlexColumn, Form, Input, TextBody } from "@opensea/ui-kit"
import { useTranslations } from "next-intl"
import { useEffect } from "react"
import { useForm, useWatch } from "react-hook-form"
import { z } from "zod"
import { useNumericTraitsQueryParam } from "@/hooks/query-param/use-numeric-traits-query-param"
import { isFormSubmitDisabled } from "@/lib/form"

export const TraitFiltersNumericTraitInputFragment = graphql(`
  fragment TraitFiltersNumericTraitInputFragment on CollectionAttribute {
    traitType
    range {
      min
      max
    }
  }
`)

type Props = {
  attribute: FragmentOf<typeof TraitFiltersNumericTraitInputFragment>
}

export function TraitFiltersNumericTraitInput({
  attribute: attributeFragment,
}: Props) {
  const t = useTranslations("TraitFiltersNumericTraitInput")
  const attribute = readFragment(
    TraitFiltersNumericTraitInputFragment,
    attributeFragment,
  )
  const { numericTraits, onSelectNumericTrait } = useNumericTraitsQueryParam()

  const matchedTraitType = numericTraits.find(
    v => v.traitType === attribute.traitType,
  )

  const { register, handleSubmit, formState, setValue, trigger, control } =
    useForm<NumericTraitPanelFormValues>({
      defaultValues: {
        min: matchedTraitType?.min ?? undefined,
        max: matchedTraitType?.max ?? undefined,
        rangeMin: attribute.range?.min ?? 0,
        rangeMax: attribute.range?.max ?? 0,
      },
      resolver: zodResolver(numericTraitPanelSchema),
      mode: "onChange",
    })

  const { errors } = formState

  const onSubmit = handleSubmit(async (data: NumericTraitPanelFormValues) => {
    if (data.min !== undefined && data.max !== undefined) {
      await onSelectNumericTrait(attribute.traitType, data.min, data.max)
    }
  })

  useEffect(() => {
    setValue("min", matchedTraitType?.min)
    setValue("max", matchedTraitType?.max)
  }, [matchedTraitType, setValue])

  const min = useWatch({ control, name: "min" })
  const max = useWatch({ control, name: "max" })

  useEffect(() => {
    void trigger(["min", "max"])
  }, [min, max, trigger])

  const minError = min ? errors.min?.message : null
  const maxError = max ? errors.max?.message : null

  const errorMessage = minError ?? maxError

  return (
    <Form noValidate onSubmit={onSubmit}>
      <FlexColumn className="gap-4">
        <FlexColumn className="gap-2">
          <FlexCenter>
            <Input
              className="w-full"
              error={Boolean(minError)}
              placeholder={attribute.range?.min.toString()}
              {...register("min", {
                setValueAs: value =>
                  value !== "" ? Number.parseFloat(value) : undefined,
              })}
            />
            <TextBody className="mx-6" weight="semibold">
              {t("to")}
            </TextBody>
            <Input
              className="w-full"
              error={Boolean(maxError)}
              placeholder={attribute.range?.max.toString()}
              {...register("max", {
                setValueAs: value =>
                  value !== "" ? Number.parseFloat(value) : undefined,
              })}
            />
          </FlexCenter>
          {errorMessage ? (
            <TextBody color="error-1" size="sm">
              {errorMessage}
            </TextBody>
          ) : null}
        </FlexColumn>

        <Button
          disabled={isFormSubmitDisabled(formState)}
          type="submit"
          variant="secondary"
        >
          {t("apply")}
        </Button>
      </FlexColumn>
    </Form>
  )
}

type NumericTraitPanelFormValues = Omit<
  z.infer<typeof numericTraitPanelSchema>,
  "min" | "max"
> & {
  min?: number
  max?: number
}

const numericTraitPanelSchema = z
  .object({
    min: z.float64().optional(),
    max: z.float64().optional(),
    rangeMin: z.float64(),
    rangeMax: z.float64(),
  })
  .transform(data => ({
    ...data,
    min: data.min ?? data.rangeMin,
    max: data.max ?? data.rangeMax,
  }))
  .superRefine((data, ctx) => {
    // Check min <= max
    if (data.min > data.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Min must be less than max",
        path: ["min"],
      })
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Min must be less than max",
        path: ["max"],
      })
    }

    // Check min >= rangeMin (with interpolated message)
    if (data.min < data.rangeMin) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Min must be greater than ${data.rangeMin.toString()}`,
        path: ["min"],
      })
    }

    // Check max <= rangeMax (with interpolated message)
    if (data.max > data.rangeMax) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Max must be less than ${data.rangeMax.toString()}`,
        path: ["max"],
      })
    }
  })
